import {PackageStatus} from '@sollapay/enums';
import {useState} from 'react';

import {useTrustAccountPermissions} from '@/features/trust-accounts/providers';

import {useResubmitPackageMutation} from './useTrustAccountActivation';

import type {ActivationPackageViewModel} from './useActivationViewModel';
import type {PackageType} from '@sollapay/enums';

export interface UseClarificationResubmitParams {
  trustAccountId: string;
  packages: ActivationPackageViewModel[];
  activeTab: PackageType;
}

/** The active package's clarification state and its resubmit action, for the package modal. */
export function useClarificationResubmit({
  trustAccountId,
  packages,
  activeTab
}: UseClarificationResubmitParams) {
  const {canSubmitForReview} = useTrustAccountPermissions();
  const {mutate, reset, isPending, isError, variables} = useResubmitPackageMutation(trustAccountId);
  const [resubmittedType, setResubmittedType] = useState<PackageType | null>(null);

  const activePackage = packages.find(p => p.type === activeTab);
  const clarifiedPackage =
    activePackage?.status === PackageStatus.CLARIFICATION_REQUIRED ? activePackage : undefined;

  return {
    activePackage,
    clarifiedPackage,
    canResubmit: Boolean(clarifiedPackage) && canSubmitForReview,
    isResubmitting: isPending,
    didResubmitFail: isError && variables === activeTab,
    resubmittedType,
    isResubmitted: resubmittedType === activeTab,
    resubmit: () => mutate(activeTab, {onSuccess: () => setResubmittedType(activeTab)}),
    reset: () => {
      reset();
      setResubmittedType(null);
    }
  };
}
